import { useCallback, useMemo, useReducer } from 'react';

import { LAYER_NORM_NONE } from '../../constants/layers';
import { LAYER_TYPES } from '../project-settings/types';
import { polyAreaWithHoles, polyPerimWithHoles, splitPolygon, subtractPolygon } from './cadUtils';
import { CadEditorState, CadPolygon, CadScaleRatio, CadTool, CadViewMode, UndoCommand } from './types';

const MAX_UNDO = 50;

// ── Actions ──────────────────────────────────────────────────────────────────

type CadAction =
  | { type: 'ADD_POLYGON'; points: [number, number][]; holes?: [number, number][][] }
  | { type: 'ADD_POLYGONS'; polygons: NewPolygon[] }
  | { type: 'UPDATE_POLYGON'; id: string; updates: Partial<CadPolygon> }
  | { type: 'UPDATE_POLYGONS'; ids: string[]; updates: Partial<CadPolygon> }
  | { type: 'DELETE_POLYGON'; id: string }
  | { type: 'DELETE_POLYGONS'; ids: string[] }
  | { type: 'SUBTRACT_FROM_POLYGON'; targetId: string; subtractPoints: [number, number][] }
  | { type: 'SET_TOOL'; tool: CadTool }
  | { type: 'SET_VIEW_MODE'; mode: CadViewMode }
  | { type: 'TOGGLE_SNAP' }
  | { type: 'SET_GRID_SIZE'; size: number }
  | { type: 'SET_DRAWING_POINTS'; points: [number, number][] }
  | { type: 'SET_ACTIVE_POLYGON'; index: number | null }
  | { type: 'SET_PROJECT_NAME'; name: string }
  | { type: 'RESET_STATE' }
  | { type: 'PUSH_UNDO'; command: UndoCommand }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'UPDATE_POLYGON_SILENT'; id: string; updates: Partial<CadPolygon> }
  | { type: 'ADD_MEASUREMENT_POINT'; point: [number, number] }
  | { type: 'CLEAR_MEASUREMENT' }
  | { type: 'SPLIT_POLYGON'; targetId: string; cutPoints: [number, number][] }
  // Scale actions
  | { type: 'ADD_SCALE_POINT'; point: [number, number] }
  | { type: 'CLEAR_SCALE_POINTS' }
  | { type: 'SET_SCALE'; metersPerUnit: number; scaleRatio: CadScaleRatio | null }
  | { type: 'SET_SCALE_RATIO'; metersPerUnit: number; scaleRatio: CadScaleRatio | null };

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Push to undo stack (capped at MAX_UNDO), clear redo. */
function pushUndo(state: CadEditorState, cmd: UndoCommand): Pick<CadEditorState, 'undoStack' | 'redoStack'> {
  const stack = [...state.undoStack, cmd];
  if (stack.length > MAX_UNDO) stack.shift();
  return { undoStack: stack, redoStack: [] };
}

// ── Initial state ────────────────────────────────────────────────────────────

const initialState: CadEditorState = {
  polygons: [],
  tool: 'draw',
  viewMode: '2d',
  snapEnabled: false,
  gridSize: 1,
  scaleSet: false,
  scalePoints: [],
  metersPerUnit: 1,
  scaleRatio: null,
  drawingPoints: [],
  activePolygonIndex: null,
  projectName: '',
  undoStack: [],
  redoStack: [],
  measurementPoints: [],
};

// ── Reducer ──────────────────────────────────────────────────────────────────

/** A polygon to add; unset fields get the editor's defaults (name "Polygon N", unspecified type). */
export interface NewPolygon {
  id?: string;
  points: [number, number][];
  holes?: [number, number][][];
  name?: string;
  type?: keyof typeof LAYER_TYPES;
}

function newPolygon({ id, points, holes, name, type }: NewPolygon, index: number): CadPolygon {
  return {
    id: id ?? crypto.randomUUID(),
    name: name ?? `Polygon ${index + 1}`,
    points,
    holes,
    area: polyAreaWithHoles(points, holes),
    perimeter: polyPerimWithHoles(points, holes),
    height: 1,
    color: type ? LAYER_TYPES[type] : '#888888',
    type: type ?? 'Unspecified',
    illuminated: false,
    canHoldLight: false,
    norm: LAYER_NORM_NONE,
    desiredLuminaireHeight: 8.0,
  };
}

/** Removes a batch that was added, or puts back a removed one; returns the inverse command. */
function toggleBatch(
  polygons: CadPolygon[],
  cmd: Extract<UndoCommand, { type: 'ADD_POLYGONS' | 'DELETE_POLYGONS' }>,
): [CadPolygon[], UndoCommand] {
  if (cmd.type === 'DELETE_POLYGONS') {
    return [[...polygons, ...cmd.polygons], { type: 'ADD_POLYGONS', polygonIds: cmd.polygons.map((p) => p.id) }];
  }
  const ids = new Set(cmd.polygonIds);
  return [
    polygons.filter((p) => !ids.has(p.id)),
    { type: 'DELETE_POLYGONS', polygons: polygons.filter((p) => ids.has(p.id)) },
  ];
}

/** The values of `keys` a polygon has now (what an update replaces, for undoing it). */
function currentValues(polygon: CadPolygon, keys: (keyof CadPolygon)[]): Partial<CadPolygon> {
  return Object.fromEntries(keys.map((key) => [key, polygon[key]])) as Partial<CadPolygon>;
}

/** Puts back the values of an UPDATE_POLYGONS step; returns the step that redoes / undoes that. */
function applyChanges(
  polygons: CadPolygon[],
  changes: { polygonId: string; oldValues: Partial<CadPolygon> }[],
): [CadPolygon[], UndoCommand] {
  const byId = new Map(changes.map((c) => [c.polygonId, c.oldValues]));
  const inverse: { polygonId: string; oldValues: Partial<CadPolygon> }[] = [];
  const next = polygons.map((p) => {
    const values = byId.get(p.id);
    if (!values) return p;
    inverse.push({ polygonId: p.id, oldValues: currentValues(p, Object.keys(values) as (keyof CadPolygon)[]) });
    return { ...p, ...values };
  });
  return [next, { type: 'UPDATE_POLYGONS', changes: inverse }];
}

export function cadReducer(state: CadEditorState, action: CadAction): CadEditorState {
  switch (action.type) {
    // ── Polygon mutations (tracked) ────────────────────────────────────────

    case 'ADD_POLYGON': {
      const polygon = newPolygon({ points: action.points, holes: action.holes }, state.polygons.length);
      return {
        ...state,
        polygons: [...state.polygons, polygon],
        drawingPoints: [],
        activePolygonIndex: state.polygons.length,
        ...pushUndo(state, { type: 'ADD_POLYGON', polygonId: polygon.id }),
      };
    }

    case 'ADD_POLYGONS': {
      if (action.polygons.length === 0) return state;
      const added = action.polygons.map((p, i) => newPolygon(p, state.polygons.length + i));
      return {
        ...state,
        polygons: [...state.polygons, ...added],
        drawingPoints: [],
        activePolygonIndex: null,
        ...pushUndo(state, { type: 'ADD_POLYGONS', polygonIds: added.map((p) => p.id) }),
      };
    }

    case 'UPDATE_POLYGON': {
      const existing = state.polygons.find((p) => p.id === action.id);
      if (!existing) return state;

      // Capture old values for only the keys being updated
      const oldValues = currentValues(existing, Object.keys(action.updates) as (keyof CadPolygon)[]);
      // If points changed, also store old area/perimeter so undo restores them
      if (action.updates.points) {
        oldValues.area = existing.area;
        oldValues.perimeter = existing.perimeter;
      }

      return {
        ...state,
        polygons: state.polygons.map((p) => {
          if (p.id !== action.id) return p;
          const updated = { ...p, ...action.updates };
          if (action.updates.points) {
            updated.area = polyAreaWithHoles(updated.points, updated.holes);
            updated.perimeter = polyPerimWithHoles(updated.points, updated.holes);
          }
          return updated;
        }),
        ...pushUndo(state, { type: 'UPDATE_POLYGON', polygonId: action.id, oldValues }),
      };
    }

    case 'UPDATE_POLYGONS': {
      const ids = new Set(action.ids);
      const keys = Object.keys(action.updates) as (keyof CadPolygon)[];
      const targets = state.polygons.filter((p) => ids.has(p.id));
      if (targets.length === 0 || keys.length === 0) return state;
      return {
        ...state,
        polygons: state.polygons.map((p) => (ids.has(p.id) ? { ...p, ...action.updates } : p)),
        ...pushUndo(state, {
          type: 'UPDATE_POLYGONS',
          changes: targets.map((p) => ({ polygonId: p.id, oldValues: currentValues(p, keys) })),
        }),
      };
    }

    case 'DELETE_POLYGONS': {
      const ids = new Set(action.ids);
      const removed = state.polygons.filter((p) => ids.has(p.id));
      if (removed.length === 0) return state;
      return {
        ...state,
        polygons: state.polygons.filter((p) => !ids.has(p.id)),
        activePolygonIndex: null,
        ...pushUndo(state, { type: 'DELETE_POLYGONS', polygons: removed }),
      };
    }

    case 'DELETE_POLYGON': {
      const idx = state.polygons.findIndex((p) => p.id === action.id);
      if (idx === -1) return state;
      const polygon = state.polygons[idx];
      return {
        ...state,
        polygons: state.polygons.filter((p) => p.id !== action.id),
        activePolygonIndex: null,
        ...pushUndo(state, { type: 'DELETE_POLYGON', polygon: { ...polygon }, index: idx }),
      };
    }

    case 'SUBTRACT_FROM_POLYGON': {
      const target = state.polygons.find((p) => p.id === action.targetId);
      if (!target) return state;

      // Snapshot before subtraction
      const oldPoints = target.points.map((pt) => [...pt]) as [number, number][];
      const oldHoles = target.holes?.map((h) => h.map((pt) => [...pt] as [number, number]));
      const oldArea = target.area;
      const oldPerimeter = target.perimeter;

      return {
        ...state,
        polygons: state.polygons.map((p) => {
          if (p.id !== action.targetId) return p;
          const result = subtractPolygon(p.points, p.holes, action.subtractPoints);
          if (!result) return p;
          return {
            ...p,
            points: result.points,
            holes: result.holes,
            area: polyAreaWithHoles(result.points, result.holes),
            perimeter: polyPerimWithHoles(result.points, result.holes),
          };
        }),
        drawingPoints: [],
        ...pushUndo(state, {
          type: 'SUBTRACT_FROM_POLYGON',
          polygonId: action.targetId,
          oldPoints,
          oldHoles,
          oldArea,
          oldPerimeter,
        }),
      };
    }

    // ── Silent update (no undo tracking) — used during vertex/polygon drag ─
    case 'UPDATE_POLYGON_SILENT':
      return {
        ...state,
        polygons: state.polygons.map((p) => {
          if (p.id !== action.id) return p;
          const updated = { ...p, ...action.updates };
          if (action.updates.points) {
            updated.area = polyAreaWithHoles(updated.points, updated.holes);
            updated.perimeter = polyPerimWithHoles(updated.points, updated.holes);
          }
          return updated;
        }),
      };

    // ── Manual push for drag completion ─────────────────────────────────────
    case 'PUSH_UNDO':
      return { ...state, ...pushUndo(state, action.command) };

    // ── Undo ────────────────────────────────────────────────────────────────
    case 'UNDO': {
      if (state.undoStack.length === 0) return state;
      const stack = [...state.undoStack];
      const cmd = stack.pop()!;
      let next = { ...state, undoStack: stack };

      switch (cmd.type) {
        case 'ADD_POLYGONS':
        case 'DELETE_POLYGONS': {
          const [polygons, inverse] = toggleBatch(next.polygons, cmd);
          next = { ...next, polygons, activePolygonIndex: null, redoStack: [...next.redoStack, inverse] };
          break;
        }
        case 'UPDATE_POLYGONS': {
          const [polygons, inverse] = applyChanges(next.polygons, cmd.changes);
          next = { ...next, polygons, redoStack: [...next.redoStack, inverse] };
          break;
        }
        case 'ADD_POLYGON': {
          // Undo add = remove
          const idx = next.polygons.findIndex((p) => p.id === cmd.polygonId);
          const polygon = next.polygons[idx];
          next = {
            ...next,
            polygons: next.polygons.filter((p) => p.id !== cmd.polygonId),
            activePolygonIndex: null,
            redoStack: [...next.redoStack, { type: 'DELETE_POLYGON', polygon: { ...polygon }, index: idx }],
          };
          break;
        }
        case 'DELETE_POLYGON': {
          // Undo delete = re-insert at original index
          const polys = [...next.polygons];
          polys.splice(cmd.index, 0, cmd.polygon);
          next = {
            ...next,
            polygons: polys,
            activePolygonIndex: cmd.index,
            redoStack: [...next.redoStack, { type: 'ADD_POLYGON', polygonId: cmd.polygon.id }],
          };
          break;
        }
        case 'UPDATE_POLYGON': {
          const existing = next.polygons.find((p) => p.id === cmd.polygonId);
          if (!existing) break;
          // Capture current values as redo data
          const redoValues = currentValues(existing, Object.keys(cmd.oldValues) as (keyof CadPolygon)[]);
          next = {
            ...next,
            polygons: next.polygons.map((p) => (p.id === cmd.polygonId ? { ...p, ...cmd.oldValues } : p)),
            redoStack: [...next.redoStack, { type: 'UPDATE_POLYGON', polygonId: cmd.polygonId, oldValues: redoValues }],
          };
          break;
        }
        case 'SUBTRACT_FROM_POLYGON': {
          const existing = next.polygons.find((p) => p.id === cmd.polygonId);
          if (!existing) break;
          // Store current (post-subtract) state for redo
          const redoCmd: UndoCommand = {
            type: 'SUBTRACT_FROM_POLYGON',
            polygonId: cmd.polygonId,
            oldPoints: existing.points.map((pt) => [...pt]) as [number, number][],
            oldHoles: existing.holes?.map((h) => h.map((pt) => [...pt] as [number, number])),
            oldArea: existing.area,
            oldPerimeter: existing.perimeter,
          };
          next = {
            ...next,
            polygons: next.polygons.map((p) =>
              p.id === cmd.polygonId
                ? {
                    ...p,
                    points: cmd.oldPoints,
                    holes: cmd.oldHoles,
                    area: cmd.oldArea,
                    perimeter: cmd.oldPerimeter,
                  }
                : p,
            ),
            redoStack: [...next.redoStack, redoCmd],
          };
          break;
        }
      }
      return next;
    }

    // ── Redo ────────────────────────────────────────────────────────────────
    case 'REDO': {
      if (state.redoStack.length === 0) return state;
      const stack = [...state.redoStack];
      const cmd = stack.pop()!;
      let next = { ...state, redoStack: stack };

      switch (cmd.type) {
        case 'ADD_POLYGONS':
        case 'DELETE_POLYGONS': {
          const [polygons, inverse] = toggleBatch(next.polygons, cmd);
          next = { ...next, polygons, activePolygonIndex: null, undoStack: [...next.undoStack, inverse] };
          break;
        }
        case 'UPDATE_POLYGONS': {
          const [polygons, inverse] = applyChanges(next.polygons, cmd.changes);
          next = { ...next, polygons, undoStack: [...next.undoStack, inverse] };
          break;
        }
        case 'ADD_POLYGON': {
          // Redo of "undo delete" = remove again
          const idx = next.polygons.findIndex((p) => p.id === cmd.polygonId);
          const polygon = next.polygons[idx];
          next = {
            ...next,
            polygons: next.polygons.filter((p) => p.id !== cmd.polygonId),
            activePolygonIndex: null,
            undoStack: [...next.undoStack, { type: 'DELETE_POLYGON', polygon: { ...polygon }, index: idx }],
          };
          break;
        }
        case 'DELETE_POLYGON': {
          // Redo of "undo add" = re-insert
          const polys = [...next.polygons];
          polys.splice(cmd.index, 0, cmd.polygon);
          next = {
            ...next,
            polygons: polys,
            activePolygonIndex: cmd.index,
            undoStack: [...next.undoStack, { type: 'ADD_POLYGON', polygonId: cmd.polygon.id }],
          };
          break;
        }
        case 'UPDATE_POLYGON': {
          const existing = next.polygons.find((p) => p.id === cmd.polygonId);
          if (!existing) break;
          const undoValues = currentValues(existing, Object.keys(cmd.oldValues) as (keyof CadPolygon)[]);
          next = {
            ...next,
            polygons: next.polygons.map((p) => (p.id === cmd.polygonId ? { ...p, ...cmd.oldValues } : p)),
            undoStack: [...next.undoStack, { type: 'UPDATE_POLYGON', polygonId: cmd.polygonId, oldValues: undoValues }],
          };
          break;
        }
        case 'SUBTRACT_FROM_POLYGON': {
          const existing = next.polygons.find((p) => p.id === cmd.polygonId);
          if (!existing) break;
          const undoCmd: UndoCommand = {
            type: 'SUBTRACT_FROM_POLYGON',
            polygonId: cmd.polygonId,
            oldPoints: existing.points.map((pt) => [...pt]) as [number, number][],
            oldHoles: existing.holes?.map((h) => h.map((pt) => [...pt] as [number, number])),
            oldArea: existing.area,
            oldPerimeter: existing.perimeter,
          };
          next = {
            ...next,
            polygons: next.polygons.map((p) =>
              p.id === cmd.polygonId
                ? {
                    ...p,
                    points: cmd.oldPoints,
                    holes: cmd.oldHoles,
                    area: cmd.oldArea,
                    perimeter: cmd.oldPerimeter,
                  }
                : p,
            ),
            undoStack: [...next.undoStack, undoCmd],
          };
          break;
        }
      }
      return next;
    }

    // ── UI-only actions (not tracked) ───────────────────────────────────────

    case 'SPLIT_POLYGON': {
      const target = state.polygons.find((p) => p.id === action.targetId);
      if (!target) return state;
      const pieces = splitPolygon(target.points, target.holes, action.cutPoints);
      if (!pieces || pieces.length < 2) return state;

      const targetIdx = state.polygons.findIndex((p) => p.id === action.targetId);
      const newPolygons = [...state.polygons];

      // Find largest piece
      let bestIdx = 0;
      let bestArea = 0;
      pieces.forEach((piece, i) => {
        const a = polyAreaWithHoles(piece.points, piece.holes);
        if (a > bestArea) {
          bestArea = a;
          bestIdx = i;
        }
      });

      // Update original to largest piece
      const largest = pieces[bestIdx];
      newPolygons[targetIdx] = {
        ...target,
        points: largest.points,
        holes: largest.holes,
        area: polyAreaWithHoles(largest.points, largest.holes),
        perimeter: polyPerimWithHoles(largest.points, largest.holes),
      };

      // Create new polygons for remaining pieces
      const newIds: string[] = [];
      pieces.forEach((piece, i) => {
        if (i === bestIdx) return;
        const newPoly: CadPolygon = {
          id: crypto.randomUUID(),
          name: `${target.name} (split ${i + 1})`,
          points: piece.points,
          holes: piece.holes,
          area: polyAreaWithHoles(piece.points, piece.holes),
          perimeter: polyPerimWithHoles(piece.points, piece.holes),
          height: target.height,
          color: target.color,
          type: target.type,
          illuminated: target.illuminated,
          canHoldLight: target.canHoldLight,
          norm: target.norm,
          desiredLuminaireHeight: target.desiredLuminaireHeight,
        };
        newIds.push(newPoly.id);
        newPolygons.push(newPoly);
      });

      return {
        ...state,
        polygons: newPolygons,
        drawingPoints: [],
        activePolygonIndex: targetIdx,
        ...pushUndo(state, {
          type: 'SPLIT_POLYGON',
          originalPolygon: { ...target },
          originalIndex: targetIdx,
          newPolygonIds: newIds,
        }),
      };
    }

    case 'ADD_MEASUREMENT_POINT':
      return { ...state, measurementPoints: [...state.measurementPoints, action.point] };
    case 'CLEAR_MEASUREMENT':
      return { ...state, measurementPoints: [] };

    // ── Scale actions ──────────────────────────────────────────────────────
    case 'ADD_SCALE_POINT':
      if (state.scalePoints.length >= 2) return state;
      return { ...state, scalePoints: [...state.scalePoints, action.point] };
    case 'CLEAR_SCALE_POINTS':
      return { ...state, scalePoints: [] };
    case 'SET_SCALE':
      return {
        ...state,
        scaleSet: true,
        metersPerUnit: action.metersPerUnit,
        scaleRatio: action.scaleRatio,
        scalePoints: [],
        tool: 'draw',
      };
    case 'SET_SCALE_RATIO':
      return {
        ...state,
        scaleSet: true,
        metersPerUnit: action.metersPerUnit,
        scaleRatio: action.scaleRatio,
      };

    case 'SET_TOOL':
      return {
        ...state,
        tool: action.tool,
        drawingPoints: [],
        measurementPoints: [],
        // Clear scale points when switching away from set-scale
        scalePoints: action.tool === 'set-scale' ? [] : state.scalePoints,
      };
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.mode };
    case 'TOGGLE_SNAP':
      return { ...state, snapEnabled: !state.snapEnabled };
    case 'SET_GRID_SIZE':
      return { ...state, gridSize: action.size };
    case 'SET_DRAWING_POINTS':
      return { ...state, drawingPoints: action.points };
    case 'SET_ACTIVE_POLYGON':
      return { ...state, activePolygonIndex: action.index };
    case 'SET_PROJECT_NAME':
      return { ...state, projectName: action.name };
    case 'RESET_STATE':
      return { ...initialState, projectName: state.projectName };
    default:
      return state;
  }
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/** A plan can become a 3D model: it has polygons, and each lit one has a lighting norm. */
const isPlanValid = (polygons: CadPolygon[]) =>
  polygons.length > 0 && polygons.every((p) => !p.illuminated || (!!p.norm && p.norm !== LAYER_NORM_NONE));

/** The parts of the editor state a saved drawing holds. */
export type CadPlan = Pick<CadEditorState, 'polygons' | 'metersPerUnit' | 'scaleRatio' | 'scaleSet'>;

/** A fresh editor, or one opened on a saved drawing (select tool, no undo history). */
const openEditor = (plan?: CadPlan): CadEditorState =>
  plan
    ? {
        ...initialState,
        tool: 'select',
        polygons: plan.polygons,
        metersPerUnit: plan.metersPerUnit,
        scaleRatio: plan.scaleRatio,
        scaleSet: plan.scaleSet,
      }
    : initialState;

export function useCadState(plan?: CadPlan) {
  const [state, dispatch] = useReducer(cadReducer, plan, openEditor);

  const addPolygon = useCallback((points: [number, number][], holes?: [number, number][][]) => {
    dispatch({ type: 'ADD_POLYGON', points, holes });
  }, []);

  const addPolygons = useCallback((polygons: NewPolygon[]) => {
    dispatch({ type: 'ADD_POLYGONS', polygons });
  }, []);

  const updatePolygon = useCallback((id: string, updates: Partial<CadPolygon>) => {
    dispatch({ type: 'UPDATE_POLYGON', id, updates });
  }, []);

  /** Update polygon without pushing to undo stack — use during drag frames. */
  const updatePolygonSilent = useCallback((id: string, updates: Partial<CadPolygon>) => {
    dispatch({ type: 'UPDATE_POLYGON_SILENT', id, updates });
  }, []);

  /** Manually push an undo command (e.g., after a drag completes). */
  const pushUndoCommand = useCallback((command: UndoCommand) => {
    dispatch({ type: 'PUSH_UNDO', command });
  }, []);

  const deletePolygon = useCallback((id: string) => {
    dispatch({ type: 'DELETE_POLYGON', id });
  }, []);

  /** The same change to several polygons, undone in one step. */
  const updatePolygons = useCallback((ids: string[], updates: Partial<CadPolygon>) => {
    dispatch({ type: 'UPDATE_POLYGONS', ids, updates });
  }, []);

  const deletePolygons = useCallback((ids: string[]) => {
    dispatch({ type: 'DELETE_POLYGONS', ids });
  }, []);

  const subtractFromPolygon = useCallback((targetId: string, subtractPoints: [number, number][]) => {
    dispatch({ type: 'SUBTRACT_FROM_POLYGON', targetId, subtractPoints });
  }, []);

  const setTool = useCallback((tool: CadTool) => {
    dispatch({ type: 'SET_TOOL', tool });
  }, []);

  const setViewMode = useCallback((mode: CadViewMode) => {
    dispatch({ type: 'SET_VIEW_MODE', mode });
  }, []);

  const toggleSnap = useCallback(() => {
    dispatch({ type: 'TOGGLE_SNAP' });
  }, []);

  const setGridSize = useCallback((size: number) => {
    dispatch({ type: 'SET_GRID_SIZE', size });
  }, []);

  const setDrawingPoints = useCallback((points: [number, number][]) => {
    dispatch({ type: 'SET_DRAWING_POINTS', points });
  }, []);

  const setActivePolygon = useCallback((index: number | null) => {
    dispatch({ type: 'SET_ACTIVE_POLYGON', index });
  }, []);

  const setProjectName = useCallback((name: string) => {
    dispatch({ type: 'SET_PROJECT_NAME', name });
  }, []);

  const splitFromPolygon = useCallback((targetId: string, cutPoints: [number, number][]) => {
    dispatch({ type: 'SPLIT_POLYGON', targetId, cutPoints });
  }, []);

  const addMeasurementPoint = useCallback((point: [number, number]) => {
    dispatch({ type: 'ADD_MEASUREMENT_POINT', point });
  }, []);

  const clearMeasurement = useCallback(() => {
    dispatch({ type: 'CLEAR_MEASUREMENT' });
  }, []);

  // ── Scale callbacks ──────────────────────────────────────────────────────

  const addScalePoint = useCallback((point: [number, number]) => {
    dispatch({ type: 'ADD_SCALE_POINT', point });
  }, []);

  const clearScalePoints = useCallback(() => {
    dispatch({ type: 'CLEAR_SCALE_POINTS' });
  }, []);

  const setScale = useCallback((metersPerUnit: number, scaleRatio: CadScaleRatio | null) => {
    dispatch({ type: 'SET_SCALE', metersPerUnit, scaleRatio });
  }, []);

  const setScaleRatio = useCallback((metersPerUnit: number, scaleRatio: CadScaleRatio | null) => {
    dispatch({ type: 'SET_SCALE_RATIO', metersPerUnit, scaleRatio });
  }, []);

  const resetState = useCallback(() => {
    dispatch({ type: 'RESET_STATE' });
  }, []);

  const undo = useCallback(() => {
    dispatch({ type: 'UNDO' });
  }, []);

  const redo = useCallback(() => {
    dispatch({ type: 'REDO' });
  }, []);

  const totalArea = useMemo(() => state.polygons.reduce((s, p) => s + p.area, 0), [state.polygons]);

  const isValid = useMemo(() => isPlanValid(state.polygons), [state.polygons]);

  const canUndo = state.undoStack.length > 0;
  const canRedo = state.redoStack.length > 0;

  return {
    state,
    addPolygon,
    addPolygons,
    updatePolygon,
    updatePolygons,
    updatePolygonSilent,
    pushUndoCommand,
    deletePolygon,
    deletePolygons,
    subtractFromPolygon,
    splitFromPolygon,
    setTool,
    setViewMode,
    toggleSnap,
    setGridSize,
    setDrawingPoints,
    setActivePolygon,
    setProjectName,
    addMeasurementPoint,
    clearMeasurement,
    addScalePoint,
    clearScalePoints,
    setScale,
    setScaleRatio,
    resetState,
    undo,
    redo,
    canUndo,
    canRedo,
    totalArea,
    isValid,
  };
}
